import { IconButton, Menu, useMenu } from "@kaltura/ds-react-components";
import { ReactElement, useState, useId } from "react";
import { MoreHorizontal24Icon } from "@kaltura/ds-react-icons";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { isMobileDevice, translate } from "@kaltura/mediaspace-shared-utils";
import {
    Analytics,
    ButtonClickAnalyticsType,
    MenuItemOnCallbackDataProps,
    SharedMenuItemProps,
} from "@kaltura/mediaspace-shared-types";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { CustomComponent } from "../custom-component/CustomComponent";

export interface ActionsMenuProps {
    items: KmsTypeComponent<any, SharedMenuItemProps>[];
    icon?: ReactElement;
    analyticsData: Analytics;
    onInvalidate?: () => void;
    onCallback?: (data: MenuItemOnCallbackDataProps) => void;
}

/**
 * actions menu (icon, with items like "edit", "delete" etc.)
 */
export const ActionsMenu = ({
    items,
    analyticsData,
    icon = <MoreHorizontal24Icon />,
    onInvalidate,
    onCallback,
}: ActionsMenuProps) => {
    const [itemState, setItemState] = useState<Record<string, unknown>[]>([]);

    const uniqueId = useId();
    const buttonId = `actions-button_${uniqueId}`;

    const sendButtonAnalytics = useButtonAnalytics();

    const { close, buttonProps, menuProps } = useMenu({
        onOpen: () => sendButtonAnalytics(analyticsData.action, ButtonClickAnalyticsType.MENU, analyticsData.mediaId),
    });

    const itemsToShow = items.filter((item) => (isMobileDevice ? !item.props.hideOnMobile : true));

    if (!itemsToShow.length) {
        return null;
    }

    /**
     * allow menu items to have menu level state.
     * its being sent back to the same menu item as props.
     */
    const handleItemStateChange = (itemStateId: number, key: string, value: unknown) => {
        setItemState((current) => ({
            ...current,
            [itemStateId]: { ...current[itemStateId], [key]: value },
        }));
    };

    return (
        <>
            <IconButton
                {...buttonProps}
                id={buttonId}
                variant={"borderless"}
                size={"medium"}
                color={"secondary"}
                aria-label={translate("show actions")}
            >
                {icon}
            </IconButton>

            <Menu
                {...menuProps}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                slotProps={{
                    list: {
                        "aria-labelledby": buttonId,
                    },
                }}
            >
                {itemsToShow.map((component, index) => {
                    // get relevant menu item state and pass it as props
                    const itemStateProps = itemState[index];

                    return (
                        <CustomComponent
                            key={index}
                            component={component}
                            errorFallback={<></>}
                            {...itemStateProps}
                            onCloseMenu={close}
                            onItemStateChange={(key: string, value: unknown) =>
                                handleItemStateChange(index, key, value)
                            }
                            reloadMedia={onInvalidate}
                            onCallback={onCallback}
                        />
                    );
                })}
            </Menu>
        </>
    );
};
